// Portland State University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Portland State's newest posted Common Data Set is 2022-2023 (Office of
//    Institutional Research and Planning, Common Data Set Archive; the
//    archive page and the Statistical Look page link no newer edition),
//    three cycles old, so it is not used. The first-year counts come from
//    the U.S. Department of Education's IPEDS Admissions survey for Fall
//    2024, first-time degree-seeking students (College Navigator and the
//    ADM2024 complete data file, unit 209807). IPEDS gives no residency
//    split, so this page has none.
//  - pdx.edu admissions: First Year (requirements, test policy) and
//    Important Undergraduate Dates & Deadlines (the fall 2027 application
//    table, decision timing, enrollment confirmation dates, programs with a
//    separate application); Admissions Policies (the enrollment deposit).
//  - PSU Student Finance, Cost of Attendance. The page gives no year label;
//    its tuition and fees match the 2026-2027 rates for 16 hours a term
//    over three terms plus the 2026-2027 mandatory fees (Tuition & Fees:
//    2026-2027 Academic Year page,
//    https://www.pdx.edu/student-finance/tuition-fees-2026-2027-academic-year;
//    the 2025-2026 rates give lower figures), so the page labels it
//    2026-2027. The cost block takes one source, so the rates page is named
//    here and in the evidence, not in SOURCES. PSU publishes an on-campus budget for
//    Oregon residents only; the nonresident budget is off campus.
//  - College Scorecard API, school id 209807 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=209807#admsns',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Portland State Admissions, First Year',
        url: 'https://www.pdx.edu/admissions/first-year',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Portland State Admissions, Important Undergraduate Dates & Deadlines',
        url: 'https://www.pdx.edu/admissions/deadlines',
        read: '2026-10-01',
    },
    policies: {
        label: 'Portland State Admissions, Admissions Policies',
        url: 'https://www.pdx.edu/admissions/admissions-policies',
        read: '2026-10-01',
    },
    coa: {
        label: 'Portland State Student Finance, Cost of Attendance',
        url: 'https://www.pdx.edu/student-finance/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?209807-Portland-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'portland-state-university',
    kbId: 'portland_state_university',
    name: 'Portland State University',
    shortName: 'Portland State',
    aliases: ['Portland State'],
    location: 'Portland, OR',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Portland State brand colors page); decoration only.
    branding: {
        primary: '#6D8D24',
        secondary: '#CFD82D',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/portland_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 9077,
        admitted: 8248,
        enrolled: 1502,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Portland State has not posted a Common Data Set since 2022-2023, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole university and none by major.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most majors need only admission to Portland State. Some ask for a separate application:',
        rows: [
            { school: 'Music (Composition, Education, History, Jazz, Performance, Theory, Voice)', requirement: 'A separate application or audition for admission.' },
            { school: 'Architecture, Art Practice, Graphic Design, Civil, Computer, Electrical, Environmental and Mechanical Engineering, Elementary Education, Special Education, Social Work', requirement: 'A separate application to enroll in restricted upper-division coursework. Each program\'s page gives its deadlines, prerequisite courses and requirements.' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application deadline', value: 'August 1, 2027' },
        rows: [
            { plan: 'Priority date (Tuition-Free Degree, with the FAFSA)', deadline: 'March 1', decision: null, binding: false },
            { plan: 'Application deadline', deadline: 'August 1, 2027', decision: '4 to 6 weeks after all documents arrive', binding: false },
        ],
        note: 'Applications for fall 2027 open August 1, 2026, and documents are due by August 5, 2027; the term begins September 27, 2027. Priority goes to applicants who apply by the dates given. March 1 is also the deadline for PSU general and most departmental scholarships, and August 1 the deadline to be considered for many of PSU\'s free and discounted tuition programs. The University Honors College has its own deadlines. Admitted students confirm enrollment by June 15 for priority, and by September 20, 2027 at the latest.',
        source: 'deadlines',
        binding: 'Admission is not binding. Students admitted for fall confirm enrollment with a $200 enrollment tuition deposit, which some students do not have to pay.',
        bindingSource: 'policies',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Portland State is test-optional: sending SAT or ACT scores is optional and not needed for an admission decision. You need 15 units of college preparatory courses with grades of C- or better, and applicants with a GPA below 3.0 are considered through holistic review.',
        source: 'firstYear',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state, on campus', 'Out-of-state, off campus'],
        rows: [
            { item: 'Tuition and fees', amounts: [13593, 36249] },
            { item: 'Housing and food', amounts: [14763, 19566] },
            { item: 'Other living expenses', amounts: [1080, 2880] },
            { item: 'Books and supplies', amounts: [900, 900] },
            { item: 'Transportation', amounts: [3348, 3348] },
            { item: 'Personal expenses', amounts: [3078, 3078] },
            { item: 'Loan origination fees', amounts: [87, 87] },
        ],
        totals: [36849, 66108],
        note: 'Portland State\'s estimated budgets for an Oregon resident living on campus and a nonresident not living on campus; it publishes no on-campus budget for nonresidents. The budget page gives no year; its tuition and fees match PSU\'s published 2026-2027 rates for a 16-hour load in each of fall, winter and spring, plus mandatory fees. Students in the Western Undergraduate Exchange or the Washington Border Discount pay lower nonresident tuition.',
        source: 'coa',
        netPrice: {
            average: 8337,
            byIncome: [
                { income: '$0 to $30,000', amount: 6255 },
                { income: '$30,001 to $48,000', amount: 5757 },
                { income: '$48,001 to $75,000', amount: 7522 },
                { income: '$75,001 to $110,000', amount: 10215 },
                { income: 'Over $110,000', amount: 17621 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Portland State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
